/**
 * @fileoverview The session rows a program's Sessions tab and the org
 * Dashboard's Lessons tab both print: date, class, who taught it and whether
 * it was written up, one word each (lib/orgSessions). Never a summary: the
 * list says the work was done, not what was said.
 *
 * The org-wide list names each row's program too; inside one program that
 * would repeat the page title on every line, so it is the caller's choice.
 * An archived session (useOrgSessionActions) says so beside its status.
 */

import { Card, ListRow, Tag } from "@/components/studio/primitives";
import { lessonStatus } from "@/lib/lessonHistory";
import { clearanceLine, clearanceTag } from "@/lib/notesClearance";
import { STATUS_TAGS, shortDate } from "@/lib/orgSessions";

const note = "px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]";

/**
 * @param {{
 *   loaded: boolean, failed?: boolean, total: number, rows: Array<object>,
 *   showProgram?: boolean, onOpenClass?: (classId: string) => void,
 *   emptyText: string, testId: string, rowTestId: string, className?: string,
 *   header?: any, leadingFor?: (row: object) => any,
 * }} props `header` sits above the rows (the bulk-clear toolbar) and
 *   `leadingFor` puts a control before a row (its tick box).
 */
export default function SessionList({
  loaded,
  failed,
  total,
  rows,
  showProgram = false,
  onOpenClass,
  emptyText,
  testId,
  rowTestId,
  className = "mb-6",
  header,
  leadingFor,
}) {
  return (
    <Card padding={0} className={className} data-testid={testId}>
      {loaded && !failed && rows.length > 0 && header}
      {!loaded ? (
        <div className={note}>Loading…</div>
      ) : failed ? (
        <div className={note}>Couldn&rsquo;t load the sessions.</div>
      ) : rows.length === 0 ? (
        <div className={note}>{total === 0 ? emptyText : "Nothing matches these filters."}</div>
      ) : (
        rows.map((r, i) => {
          // An instructor's request to clear a missing session outranks the
          // plain "No notes": it is what the admin has to answer.
          const tag = clearanceTag(r.notes_clearance) || STATUS_TAGS[r.session_status];
          const status = tag ? <Tag accent={tag.accent}>{tag.label}</Tag> : r.shared === "yes" ? <Tag accent>Shared</Tag> : null;
          return (
            <ListRow
              key={r.lesson_id}
              borderBottom={i < rows.length - 1}
              data-testid={`${rowTestId}-${r.lesson_id}`}
              leading={leadingFor?.(r)}
              title={r.kind === "class" ? r.class_name : r.title || "Solo lesson"}
              subtitle={[
                r.instructor_name,
                showProgram ? r.program_title || null : null,
                r.substitute_name ? `Covered by ${r.substitute_name}` : null,
                r.attendance !== "" && r.kind === "class" ? `${r.attendance}/${r.roster_size} attended` : null,
                lessonStatus(r) === "written" && r.kind === "class" && r.title ? r.title : null,
                clearanceLine(r.notes_clearance),
              ]
                .filter(Boolean)
                .join(" · ")}
              meta={[shortDate(r.date), r.time_local].filter(Boolean).join(" · ")}
              trailing={
                r.archived ? (
                  <span className="flex items-center gap-2">
                    <Tag data-testid={`${rowTestId}-archived-${r.lesson_id}`}>Archived</Tag>
                    {status}
                  </span>
                ) : (
                  status || undefined
                )
              }
              onPress={r.class_id && onOpenClass ? () => onOpenClass(r.class_id) : undefined}
            />
          );
        })
      )}
    </Card>
  );
}
